import React, { useEffect, useState } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"
import { WandSparkles } from "lucide-react"

import type { PersonaMetadata } from "@/types/api/Persona"
import { cn } from "@/utils/cn"

import type { usePersonaVoiceDesign } from "../hooks/usePersonaVoiceDesign"
import type { PersonaCustomVoiceOption } from "../types"
import {
  getMetadataForLanguageSelection,
  getMetadataForVoiceSelection,
} from "../utils/personaModalUtils"
import { PersonaCustomVoiceFields } from "./PersonaCustomVoiceFields"
import { PersonaVoiceLibrary } from "./PersonaVoiceLibrary"

type PersonaVoiceDesignController = ReturnType<typeof usePersonaVoiceDesign>

export interface PersonaVoiceSelection {
  voice_name: string | undefined
  language_code: string | undefined
  voice_volume: number | undefined
  voice_speed: number | undefined
  custom_voice_id: string | undefined
  custom_voice_name: string | undefined
  custom_voice_description: string | undefined
  custom_voice_language: string | undefined
}

interface PersonaVoicePickerDialogProps {
  isOpen: boolean
  metadata?: PersonaMetadata
  isNewPersona: boolean
  currentlyPlayingAudioId: string | null
  onPlayingChange: (playingId: string | null) => void
  onCancel: () => void
  onApply: (selection: PersonaVoiceSelection) => void
  /** Custom voices this persona can pick from, newest first. */
  customVoices: PersonaCustomVoiceOption[]
  /** Whether the "Generate your own" card appears in the library. */
  canGenerateCustomVoice: boolean
  voiceDesign: PersonaVoiceDesignController
  /** Called when the designer finishes a voice, so the caller can keep it. */
  onCustomVoiceCreated: (voice: PersonaCustomVoiceOption) => void
}

/**
 * Voice catalog in a modal, the way the launcher picks agents and test sets.
 * Browsing, volume, and speed all edit a draft, so the persona only changes
 * when the user applies. "Generate your own" swaps the body for the voice
 * designer in place; a finished voice lands in the draft as the selection.
 */
export function PersonaVoicePickerDialog({
  isOpen,
  metadata,
  isNewPersona,
  currentlyPlayingAudioId,
  onPlayingChange,
  onCancel,
  onApply,
  customVoices,
  canGenerateCustomVoice,
  voiceDesign,
  onCustomVoiceCreated,
}: PersonaVoicePickerDialogProps) {
  const [draft, setDraft] = useState<PersonaMetadata | undefined>(metadata)
  const [isDesignerOpen, setIsDesignerOpen] = useState(false)

  // Reseed the draft each time the dialog opens so a cancelled browse never
  // leaks into the next one.
  useEffect(() => {
    if (isOpen) {
      setDraft(metadata)
      setIsDesignerOpen(false)
    }
  }, [isOpen, metadata])

  const canApply = Boolean(
    draft?.custom_voice_id || (draft?.voice_name && draft?.language_code),
  )

  const openDesigner = () => {
    voiceDesign.cancelDesigning()
    voiceDesign.startDesigning()
    setIsDesignerOpen(true)
  }

  const closeDesigner = () => {
    voiceDesign.cancelDesigning()
    onPlayingChange(null)
    setIsDesignerOpen(false)
  }

  const selectCustomVoice = (voice: PersonaCustomVoiceOption) => {
    onPlayingChange(null)
    setDraft((previous) => ({
      ...previous,
      custom_voice_id: voice.id,
      custom_voice_name: voice.name,
      custom_voice_description: voice.description,
      custom_voice_language: voice.language,
      voice_name: undefined,
    }))
  }

  const handleCancel = () => {
    // A voice mid-save must not be abandoned by closing the dialog.
    if (isDesignerOpen && voiceDesign.keepingEmbeddingId !== null) {
      return
    }
    if (isDesignerOpen) {
      voiceDesign.cancelDesigning()
    }
    onCancel()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      {/* The dialog is bounded by the viewport rather than by its contents, so
          a wrapped header band or a tall catalog eats into the scroll region
          instead of pushing the footer's actions off-screen. The catalog takes
          a fixed share of that height, the way the metric picker does; the
          designer stays as tall as its form needs. */}
      <DialogContent
        className={cn(
          "max-h-[calc(100dvh-2rem)] w-full max-w-6xl",
          !isDesignerOpen && "h-[84dvh]",
        )}
      >
        {isDesignerOpen ? (
          <>
            <DialogHeader className="border-b border-coval-border px-6 py-5">
              <DialogTitle className="text-xl">
                Create a custom voice
              </DialogTitle>
              <DialogDescription>
                Design a voice from a description and add it to your library.
              </DialogDescription>
            </DialogHeader>
            {/* The designer is a form, not a catalog, so it keeps a readable
                measure inside the width the catalog needs. */}
            <DialogBody className="mx-auto w-full max-w-3xl px-6 py-5">
              <PersonaCustomVoiceFields
                currentlyPlayingAudioId={currentlyPlayingAudioId}
                onPlayingChange={onPlayingChange}
                voiceDesign={voiceDesign}
                onCreated={(voice) => {
                  onCustomVoiceCreated(voice)
                  selectCustomVoice(voice)
                  setIsDesignerOpen(false)
                }}
                onCancel={closeDesigner}
              />
            </DialogBody>
          </>
        ) : (
          <>
            {/* The library owns the body's gutters, header band, and scroll
                region, so the body adds none of its own — the same handoff the
                metric picker makes to its table. */}
            <DialogBody className="min-h-0 overflow-hidden p-0">
              <PersonaVoiceLibrary
                title={
                  <DialogTitle className="text-xl">Choose a Voice</DialogTitle>
                }
                className="h-full"
                metadata={draft}
                isBuiltIn={false}
                isNewPersona={isNewPersona}
                currentlyPlayingAudioId={currentlyPlayingAudioId}
                onPlayingChange={onPlayingChange}
                onVoiceSelection={(voiceName) =>
                  setDraft((previous) =>
                    getMetadataForVoiceSelection(previous, voiceName),
                  )
                }
                onLanguageSelection={(languageCode) =>
                  setDraft((previous) =>
                    getMetadataForLanguageSelection(previous, languageCode),
                  )
                }
                onVoiceVolumeChange={(voice_volume) =>
                  setDraft((previous) => ({ ...previous, voice_volume }))
                }
                onVoiceSpeedChange={(voice_speed) =>
                  setDraft((previous) => ({ ...previous, voice_speed }))
                }
                customVoices={customVoices}
                selectedCustomVoiceId={draft?.custom_voice_id ?? undefined}
                customVoicePreviewUrls={voiceDesign.previewUrls}
                customVoicePreviewErrorIds={voiceDesign.previewErrorIds}
                getCustomVoicePreviewUrl={voiceDesign.getPreviewUrl}
                onCustomVoiceSelection={selectCustomVoice}
              />
            </DialogBody>
            <DialogFooter className="border-t border-coval-border px-6 py-5">
              {/* Designing a voice is an action on the catalog rather than a
                  row in it, so it takes the footer's leading edge, where the
                  entity pickers put their "create one" action. */}
              {canGenerateCustomVoice && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={openDesigner}
                  className="mr-auto"
                >
                  <WandSparkles />
                  Generate your own
                </Button>
              )}
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={!canApply}
                onClick={() =>
                  onApply({
                    voice_name: draft?.voice_name ?? undefined,
                    language_code: draft?.language_code ?? undefined,
                    voice_volume: draft?.voice_volume ?? undefined,
                    voice_speed: draft?.voice_speed ?? undefined,
                    custom_voice_id: draft?.custom_voice_id ?? undefined,
                    custom_voice_name: draft?.custom_voice_name ?? undefined,
                    custom_voice_description:
                      draft?.custom_voice_description ?? undefined,
                    custom_voice_language:
                      draft?.custom_voice_language ?? undefined,
                  })
                }
              >
                Select
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
